/**
 * MicrophoneControl - Microphone toggle and level indicator
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { AUDIO } from '../lib/constants'
import { adjustColorBrightness, lerp } from '../lib/utils'

interface MicrophoneControlProps {
  isEnabled: boolean
  level?: number
  onToggle: () => void
  onDeviceSelect?: (deviceId: string) => void
  availableDevices?: MediaDeviceInfo[]
  selectedDevice?: string | null
  showLevel?: boolean
  showDeviceSelector?: boolean
  size?: 'small' | 'medium' | 'large'
  className?: string
  disabled?: boolean
  primaryColor?: string
}

export const MicrophoneControl: React.FC<MicrophoneControlProps> = ({
  isEnabled,
  level = 0,
  onToggle,
  onDeviceSelect,
  availableDevices = [],
  selectedDevice,
  showLevel = true,
  showDeviceSelector = false,
  size = 'medium',
  className = '',
  disabled = false,
  primaryColor = '#4ade80',
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [animatedLevel, setAnimatedLevel] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const levelRef = useRef(0)
  const animationFrameRef = useRef<number | null>(null)

  // Size configurations
  const sizes = {
    small: { button: 32, icon: 16, padding: 6 },
    medium: { button: 40, icon: 20, padding: 8 },
    large: { button: 48, icon: 24, padding: 10 },
  }

  const currentSize = sizes[size]
  const darkerPrimaryColor = adjustColorBrightness(primaryColor, -40)

  // Smooth level animation
  useEffect(() => {
    const animateLevel = () => {
      levelRef.current = lerp(levelRef.current, level, 0.3)
      setAnimatedLevel(levelRef.current)

      if (Math.abs(levelRef.current - level) > 0.01) {
        animationFrameRef.current = requestAnimationFrame(animateLevel)
      }
    }

    animateLevel()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [level])

  // Handle outside clicks for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target)
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(target)

      if (clickedOutsideDropdown && clickedOutsideButton) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  // Handle device selection
  const handleDeviceSelect = useCallback(
    (deviceId: string) => {
      onDeviceSelect?.(deviceId)
      setShowDropdown(false)
    },
    [onDeviceSelect],
  )

  // Calculate level color
  const levelColor =
    animatedLevel > AUDIO.SPEAKING_THRESHOLD
      ? '#10B981' // Green when speaking
      : animatedLevel > AUDIO.SILENCE_THRESHOLD
        ? '#F59E0B' // Yellow when detecting sound
        : '#6B7280' // Gray when silent

  return (
    <div className={`voice-agent-microphone-control ${className}`} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Microphone button */}
        <button
          className='voice-agent-mic-button'
          onClick={onToggle}
          disabled={disabled}
          style={{
            position: 'relative',
            width: `${currentSize.button}px`,
            height: `${currentSize.button}px`,
            borderRadius: '50%',
            border: 'none',
            background: isEnabled ? `linear-gradient(to bottom right, ${primaryColor}, ${darkerPrimaryColor})` : '#E5E7EB',
            color: '#FFFFFF',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${currentSize.padding}px`,
            transition: 'background 0.2s, transform 0.1s',
            opacity: disabled ? 0.5 : 1,
          }}
          onMouseEnter={(e) => !disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onMouseDown={(e) => !disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
          onMouseUp={(e) => !disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
          aria-label={isEnabled ? 'Mute microphone' : 'Unmute microphone'}
          title={isEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {/* Level indicator ring */}
          {showLevel && isEnabled && (
            <div
              className='voice-agent-mic-level-ring'
              style={{
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                border: `2px solid ${levelColor}`,
                pointerEvents: 'none',
                transform: `scale(${1 + animatedLevel * 0.3})`,
                opacity: 0.3 + animatedLevel * 0.7,
                transition: 'transform 0.1s, opacity 0.1s',
              }}
            />
          )}

          {/* Microphone icon */}
          {isEnabled ? <MicrophoneIcon size={currentSize.icon} /> : <MicrophoneMutedIcon size={currentSize.icon} />}
        </button>

        {/* Device selector button */}
        {showDeviceSelector && availableDevices.length > 1 && (
          <button
            ref={buttonRef}
            className='voice-agent-device-selector-button'
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.1s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
            aria-label='Select microphone'
            title='Select microphone'
          >
            <ChevronDownIcon size={16} />
          </button>
        )}
      </div>

      {/* Level bars (visual representation) */}
      {showLevel && isEnabled && size !== 'small' && (
        <div
          className='voice-agent-mic-level-bars'
          style={{
            display: 'flex',
            gap: '2px',
            marginTop: '4px',
            height: '12px',
            alignItems: 'flex-end',
          }}
        >
          {[0.3, 0.5, 0.7, 0.9].map((threshold, index) => (
            <div
              key={index}
              className='voice-agent-mic-level-bar'
              style={{
                width: '4px',
                borderRadius: '2px',
                height: animatedLevel > threshold ? '100%' : '20%',
                backgroundColor: animatedLevel > threshold ? levelColor : '#E5E7EB',
                transition: 'height 0.1s, background-color 0.1s',
              }}
            />
          ))}
        </div>
      )}

      {/* Device dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className='voice-agent-device-dropdown'
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            left: 0,
            minWidth: '200px',
            maxWidth: '300px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            overflow: 'hidden',
            opacity: 1,
            transform: 'translateY(0)',
            animation: 'fadeInUp 0.2s ease-out',
          }}
        >
          <div style={{ padding: '4px' }}>
            {availableDevices.map((device) => (
              <button
                key={device.deviceId}
                onClick={() => handleDeviceSelect(device.deviceId)}
                className='voice-agent-device-option'
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontSize: '14px',
                  backgroundColor: device.deviceId === selectedDevice ? '#F3E8FF' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = device.deviceId === selectedDevice ? '#F3E8FF' : 'transparent'
                }}
              >
                {device.label || `Microphone ${device.deviceId.substring(0, 8)}`}
                {device.deviceId === selectedDevice && ' ✓'}
              </button>
            ))}
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}

// Icon components
const MicrophoneIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' />
    <path d='M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const MicrophoneMutedIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='currentColor'>
    <path d='M1 1l22 22M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23M12 19v4M8 23h8' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const ChevronDownIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='6 9 12 15 18 9' />
  </svg>
)

// Simplified microphone toggle for inline use
export const SimpleMicToggle: React.FC<{
  isEnabled: boolean
  onToggle: () => void
  size?: number
}> = ({ isEnabled, onToggle, size = 24 }) => (
  <button
    onClick={onToggle}
    style={{
      padding: '4px',
      backgroundColor: isEnabled ? '#7C3AED' : '#E5E7EB',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    aria-label={isEnabled ? 'Mute' : 'Unmute'}
  >
    {isEnabled ? <MicrophoneIcon size={size} /> : <MicrophoneMutedIcon size={size} />}
  </button>
)

export default MicrophoneControl
